import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmt = (iso, lang) =>
  new Intl.DateTimeFormat(lang, { day: "numeric", month: "short" }).format(new Date(`${iso}T12:00:00`));

const RangeCalendar = ({ blocked = [], checkIn, checkOut, onChange, months = 2, testidPrefix = "range" }) => {
  const { t, lang } = useLanguage();
  const [offset, setOffset] = useState(0);
  const [hover, setHover] = useState(null);
  const [highlights, setHighlights] = useState({});
  const today = new Date();
  const todayISO = toISO(today);
  const blockedSet = new Set(blocked);

  // Fetch pricing highlights for the visible + adjacent months
  useEffect(() => {
    const from = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    const to = new Date(today.getFullYear(), today.getMonth() + offset + months + 1, 0);
    axios.get(`${API}/pricing-highlights`, { params: { from_: toISO(from), to: toISO(to) } })
      .then(({ data }) => {
        const m = {};
        (data.dates || []).forEach((d) => { m[d.date] = d.category; });
        setHighlights(m);
      }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, months]);

  // Auto-scroll to check-in month if provided (deep-link, draft restore, etc.)
  useEffect(() => {
    if (!checkIn) return;
    const d = new Date(`${checkIn}T12:00:00`);
    const diff = (d.getFullYear() - today.getFullYear()) * 12 + (d.getMonth() - today.getMonth());
    if (diff >= 0 && (diff < offset || diff >= offset + months)) setOffset(diff);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn]);

  // first blocked night after check-in: check-out may land on it, not beyond
  const limit = checkIn ? blocked.filter((d) => d > checkIn).sort()[0] || null : null;
  const selectingEnd = Boolean(checkIn && !checkOut);
  const previewEnd = selectingEnd && hover && hover > checkIn ? hover : checkOut;

  const weekdays = [...Array(7)].map((_, i) =>
    new Intl.DateTimeFormat(lang, { weekday: "narrow" }).format(new Date(2026, 0, 5 + i))
  );

  const buildMonth = (off) => {
    const first = new Date(today.getFullYear(), today.getMonth() + off, 1);
    const y = first.getFullYear();
    const m = first.getMonth();
    const name = new Intl.DateTimeFormat(lang, { month: "long", year: "numeric" }).format(first);
    const cells = [];
    for (let i = 0; i < (first.getDay() + 6) % 7; i++) cells.push(null);
    for (let d = 1; d <= new Date(y, m + 1, 0).getDate(); d++) cells.push(toISO(new Date(y, m, d)));
    return { name, cells };
  };

  const isDisabled = (d) => {
    if (d < todayISO) return true;
    if (selectingEnd) return d <= checkIn ? d !== checkIn : (blockedSet.has(d) && d !== limit) || (limit && d > limit);
    return blockedSet.has(d);
  };

  const pick = (d) => {
    if (selectingEnd && d > checkIn) onChange({ check_in: checkIn, check_out: d });
    else onChange({ check_in: d, check_out: "" });
    setHover(null);
  };

  const monthList = [...Array(months)].map((_, i) => buildMonth(offset + i));

  return (
    <div data-testid={`${testidPrefix}-calendar`} className="rounded-2xl border border-[#0D253A]/15 bg-[#FBFAF7] p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div data-testid={`${testidPrefix}-summary`} className="flex items-center gap-2 text-sm">
          <span className={`px-3 py-1.5 rounded-full border ${checkIn ? "border-[#0D253A] bg-[#0D253A] text-[#F8F5F0]" : "border-dashed border-[#0D253A]/30 text-[#5C6773]"}`}>
            {checkIn ? fmt(checkIn, lang) : t.booking.checkin}
          </span>
          <span className="text-[#5C6773]">→</span>
          <span className={`px-3 py-1.5 rounded-full border ${checkOut ? "border-[#C86D51] bg-[#C86D51] text-white" : "border-dashed border-[#0D253A]/30 text-[#5C6773]"}`}>
            {checkOut ? fmt(checkOut, lang) : t.booking.checkout}
          </span>
          {checkIn && (
            <button
              type="button"
              data-testid={`${testidPrefix}-reset`}
              onClick={() => onChange({ check_in: "", check_out: "" })}
              className="p-1.5 rounded-full text-[#5C6773] hover:text-[#C86D51] transition-colors"
              aria-label={t.booking.resetDates}
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid={`${testidPrefix}-prev`} onClick={() => setOffset(Math.max(0, offset - 1))} disabled={offset === 0}
            className="p-2 rounded-full border border-[#0D253A]/15 text-[#0D253A] hover:border-[#C86D51] hover:text-[#C86D51] disabled:opacity-30 transition-colors" aria-label={t.availability.prev}>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button type="button" data-testid={`${testidPrefix}-next`} onClick={() => setOffset(offset + 1)}
            className="p-2 rounded-full border border-[#0D253A]/15 text-[#0D253A] hover:border-[#C86D51] hover:text-[#C86D51] transition-colors" aria-label={t.availability.next}>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <p data-testid={`${testidPrefix}-hint`} className="text-xs text-[#5C6773] mb-3">
        {!checkIn ? t.booking.pickCheckin : selectingEnd ? t.booking.pickCheckout : `${t.booking.datesOk}`}
      </p>
      <div className={`grid grid-cols-1 ${months > 1 ? "sm:grid-cols-2" : ""} gap-5`}>
        {monthList.map((mo) => (
          <div key={mo.name}>
            <p className="font-display text-base text-[#0D253A] capitalize mb-2">{mo.name}</p>
            <div className="grid grid-cols-7 gap-y-1 text-center">
              {weekdays.map((w, i) => (
                <span key={i} className="text-[10px] font-semibold uppercase text-[#5C6773] py-1">{w}</span>
              ))}
              {mo.cells.map((d, i) => {
                if (!d) return <span key={i} />;
                const disabled = isDisabled(d);
                const isBlocked = blockedSet.has(d);
                const isStart = d === checkIn;
                const isEnd = d === previewEnd;
                const inRange = checkIn && previewEnd && d > checkIn && d < previewEnd;
                const hi = highlights[d];
                let cls = "text-[#0D253A] hover:bg-[#1E5F74]/15";
                if (isStart || isEnd) cls = "bg-[#0D253A] text-[#F8F5F0] font-semibold";
                else if (inRange) cls = "bg-[#1E5F74]/15 text-[#0D253A] rounded-none";
                else if (isBlocked) cls = "bg-[#C86D51]/15 text-[#C86D51] line-through";
                else if (disabled) cls = "text-[#5C6773]/35";
                return (
                  <button
                    key={i}
                    type="button"
                    data-testid={`${testidPrefix}-day-${d}`}
                    disabled={disabled && !isStart}
                    onClick={() => pick(d)}
                    onMouseEnter={() => selectingEnd && !disabled && setHover(d)}
                    onMouseLeave={() => setHover(null)}
                    className={`relative aspect-square flex items-center justify-center text-xs rounded-full transition-colors duration-150 disabled:cursor-not-allowed ${cls} ${
                      d === todayISO && !isStart && !isEnd ? "ring-1 ring-[#0D253A]/40" : ""
                    } ${isStart && previewEnd ? "rounded-r-none" : ""} ${isEnd ? "rounded-l-none" : ""}`}
                  >
                    {Number(d.slice(8))}
                    {hi && !isBlocked && !isStart && !isEnd && (
                      <span
                        data-testid={`${testidPrefix}-dot-${d}`}
                        className={`absolute bottom-0.5 w-1 h-1 rounded-full ${
                          hi === "event" ? "bg-[#C86D51]" :
                          hi === "period" ? "bg-[#1E5F74]" :
                          hi === "manual" ? "bg-fuchsia-600" :
                          hi === "dynamic" ? "bg-emerald-600" :
                          "bg-amber-500"
                        }`}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RangeCalendar;
